import React, { useState, useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mic, Square, Paperclip, Send, Loader2, X } from "lucide-react";

const INPUT_STYLE = { backgroundColor: "#0A1F44", border: "1px solid #1E3A8A", color: "#fff" };

function micErrorMessage(err) {
  const name = err?.name;
  if (name === "NotAllowedError" || name === "SecurityError") {
    return "Microphone blocked. Click the camera/mic icon in your browser's address bar and allow access, then retry.";
  }
  if (name === "NotFoundError") return "No microphone found on this device.";
  return "Could not start recording. Please allow microphone access.";
}

// Encapsulates browser audio capture + transcription.
function useVoiceRecorder(onTranscript) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const transcribe = async (blob) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "voice.webm");
      const { data } = await api.post("/transcribe", fd);
      onTranscript(data.text || "");
      toast.success("Voice transcribed");
    } catch {
      toast.error("Could not transcribe audio");
    } finally {
      setTranscribing(false);
    }
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Voice input needs a secure (https) browser tab. Try opening the portal in a new browser tab.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        transcribe(new Blob(chunksRef.current, { type: "audio/webm" }));
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (err) {
      toast.error(micErrorMessage(err));
    }
  };

  const stop = () => {
    mediaRef.current?.stop();
    setRecording(false);
  };

  return { recording, transcribing, toggle: () => (recording ? stop() : start()) };
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm text-[#9CA3AF] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function VoiceButton({ recording, transcribing, onToggle }) {
  let icon = <Mic size={13} />;
  let text = "Voice input";
  if (transcribing) {
    icon = <Loader2 size={13} className="animate-spin" />;
    text = "Transcribing...";
  } else if (recording) {
    icon = <Square size={13} />;
    text = "Stop";
  }
  return (
    <button
      type="button"
      data-testid="feedback-voice-button"
      onClick={onToggle}
      disabled={transcribing}
      className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full transition-colors"
      style={{ backgroundColor: recording ? "#EF4444" : "rgba(249,115,22,0.15)", color: recording ? "#fff" : "#F97316" }}
    >
      {icon}
      {text}
    </button>
  );
}

function DocumentSelect({ documents, value, onChange }) {
  return (
    <Field label="Related document">
      <select
        data-testid="feedback-document-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg px-3 py-2.5 outline-none"
        style={INPUT_STYLE}
      >
        <option value="general">General Feedback</option>
        {documents.map((d) => (
          <option key={d.id} value={d.id}>{d.title}</option>
        ))}
      </select>
    </Field>
  );
}

function NameRow({ name, setName, anonymous, setAnonymous }) {
  return (
    <div className="flex items-end gap-3">
      <div className="flex-1">
        <Field label="Your name">
          <input
            data-testid="feedback-name-input"
            value={name}
            disabled={anonymous}
            onChange={(e) => setName(e.target.value)}
            placeholder={anonymous ? "Anonymous" : "Name"}
            className="w-full rounded-lg px-3 py-2.5 outline-none disabled:opacity-50"
            style={INPUT_STYLE}
          />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm text-[#9CA3AF] pb-3 cursor-pointer select-none">
        <input
          data-testid="feedback-anonymous-checkbox"
          type="checkbox"
          checked={anonymous}
          onChange={(e) => setAnonymous(e.target.checked)}
          className="w-4 h-4 accent-[#F97316]"
        />
        Anonymous
      </label>
    </div>
  );
}

function FeedbackTextArea({ text, setText, voice }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm text-[#9CA3AF]">Feedback</label>
        <VoiceButton recording={voice.recording} transcribing={voice.transcribing} onToggle={voice.toggle} />
      </div>
      <textarea
        data-testid="feedback-text-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        placeholder="Share your feedback..."
        className="w-full rounded-lg px-3 py-2.5 outline-none resize-y"
        style={INPUT_STYLE}
      />
    </div>
  );
}

function AttachmentRow({ attachment, setAttachment, submitting }) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <label
        data-testid="feedback-attachment-label"
        className="flex items-center gap-2 text-sm text-[#9CA3AF] cursor-pointer hover:text-white transition-colors"
      >
        <Paperclip size={16} />
        {attachment ? attachment.name : "Attach photo, PDF or Word"}
        <input
          type="file"
          data-testid="feedback-attachment-input"
          className="hidden"
          accept="image/*,.pdf,.doc,.docx"
          onChange={(e) => setAttachment(e.target.files[0] || null)}
        />
      </label>
      {attachment && (
        <button type="button" onClick={() => setAttachment(null)} className="text-[#9CA3AF] hover:text-white">
          <X size={16} />
        </button>
      )}
      <button
        type="submit"
        data-testid="feedback-submit-button"
        disabled={submitting}
        className="ml-auto flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold text-white transition-all disabled:opacity-60"
        style={{ backgroundColor: "#F97316" }}
      >
        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        Submit Feedback
      </button>
    </div>
  );
}

function buildFeedbackFormData({ documentId, documents, anonymous, name, text, attachment }) {
  const fd = new FormData();
  const doc = documentId !== "general" ? documents.find((d) => d.id === documentId) : null;
  fd.append("document_title", doc ? doc.title : "General Feedback");
  if (documentId !== "general") fd.append("document_id", documentId);
  fd.append("anonymous", anonymous ? "true" : "false");
  if (!anonymous && name) fd.append("name", name);
  fd.append("feedback_text", text);
  if (attachment) fd.append("attachment", attachment);
  return fd;
}

// Shared feedback form for the Manager portal (dark theme).
export default function FeedbackForm({ clientId, documents }) {
  const [documentId, setDocumentId] = useState("general");
  const [name, setName] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [text, setText] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const appendTranscript = (t) => setText((prev) => (prev ? prev + " " : "") + t);
  const voice = useVoiceRecorder(appendTranscript);

  const resetForm = () => {
    setText(""); setName(""); setAnonymous(false); setAttachment(null); setDocumentId("general");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() && !attachment) {
      toast.error("Please add feedback text or an attachment");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/clients/${clientId}/feedback`,
        buildFeedbackFormData({ documentId, documents, anonymous, name, text, attachment }));
      toast.success("Your feedback has been received");
      resetForm();
    } catch {
      toast.error("Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="feedback-form">
      <DocumentSelect documents={documents} value={documentId} onChange={setDocumentId} />
      <NameRow name={name} setName={setName} anonymous={anonymous} setAnonymous={setAnonymous} />
      <FeedbackTextArea text={text} setText={setText} voice={voice} />
      <AttachmentRow attachment={attachment} setAttachment={setAttachment} submitting={submitting} />
    </form>
  );
}
